// Ramp primitive: piecewise-linear t → vec3 over up to RAMP_MAX_STOPS stops.
// Stops are vec4(t, r, g, b), sorted by t; the last stop is expected at t = 1.0 (the
// UniformPacker pads short ramps with trailing repeats of the last color at t = 1.0).
export const RAMP_GLSL = `
// Piecewise-linear ramp over up to 6 stops (vec4(t, r, g, b), t ascending).  t is
// clamped to [0, 1]; stops past the last are repeats of the final color at t = 1.0.
vec3 rampVal(float t, vec4 stops[6]) {
  t = clamp(t, 0.0, 1.0);
  for (int i = 0; i < 5; i++) {
    vec4 a = stops[i];
    vec4 b = stops[i + 1];
    if (t <= b.x) {
      float span = b.x - a.x;
      float s = span > 1e-5 ? clamp((t - a.x) / span, 0.0, 1.0) : 1.0;
      return mix(a.gba, b.gba, s);
    }
  }
  return stops[5].gba;
}

// Mean colour of the ramp over t in [0, 1] (Set3 §4.2): the exact piecewise-linear integral.
// The colour-mean AA fades converge to THIS, never to a t=0.5 sample — a non-linear ramp's
// t=0.5 value is NOT its mean, which is exactly the zoomed-out "wash" (dirt/mulch/wood).
// Mirrors the JS rampMean in PrimitiveRegistry: below the first stop rampVal clamps to the
// first colour (its [0, firstT] flat tail); the padded repeats of the last colour at t=1.0
// have span 0 and contribute nothing.  Ref-backed ramps (uniform stops) call this at runtime
// so the fade tracks slider changes — a JS-baked literal would go stale (and break the D10
// free-variant property: params-only variants must share one program).
vec3 rampMeanOfStops(vec4 stops[6]) {
  vec3 acc = vec3(0.0);
  float firstT = clamp(stops[0].x, 0.0, 1.0);
  if (firstT > 1e-5) acc += firstT * stops[0].gba;
  for (int i = 0; i < 5; i++) {
    vec4 a = stops[i];
    vec4 b = stops[i + 1];
    float span = b.x - a.x;
    if (span > 1e-5) acc += span * (a.gba + b.gba) * 0.5;
  }
  return acc;
}

// Option D — the CONDITIONAL mean: the sub-pixel fade target when the layer's ramp lookup has been
// shifted by 'delta' (a slowly-varying macro modulator).  This is the single most important line of
// the whole approach, so the reasoning is worth spelling out:
//
// The correct anti-aliased colour is E[colour | pixel].  For an UNMODULATED noise layer that
// expectation is 'rampMeanOfStops' — a CONSTANT, which is exactly why zooming out collapses to one
// flat colour (nothing about a stationary field varies with position).  Once the ramp lookup is
// shifted by delta(p), the expectation becomes a FUNCTION of delta — so the fade target varies
// across the map and wide zoom keeps real structure.
//
// Form: baseMean + (rampVal(0.5 + delta) - rampVal(0.5)).  A first-order (mean-shift) approximation
// of the true shifted integral, chosen because it is EXACTLY DC-PRESERVING: at delta = 0 the
// correction term is identically zero, so the target is bit-for-bit 'baseMean' and the established
// zoom-out mean-stability contract is untouched.  0.5 is the shared mean of every fbm mode's t
// (each mode's remap is built to centre on it — see fbmVal's 'mean'), so rampVal(0.5) is the right
// linearisation point.  Two rampVal calls, no loops beyond theirs.
vec3 rampMeanShifted(vec4 stops[6], vec3 baseMean, float delta) {
  return baseMean + (rampVal(clamp(0.5 + delta, 0.0, 1.0), stops) - rampVal(0.5, stops));
}
`;
